import React, { useMemo } from "react"
import { ChevronRight } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu"
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from "@/lib/menu-chrome"

export interface BreadcrumbSegment {
  label: string
  /** Folder/file path when known. Null is fine for index crumbs that still have onClick. */
  path: string | null
  onClick?: (event?: React.MouseEvent) => void
}

interface AddressBarProps {
  segments: BreadcrumbSegment[]
  className?: string
  separator?: React.ReactNode
  containerWidth?: number | null
  maxSegments?: number // Show only last N segments when space is limited
  showEllipsis?: boolean // Show "..." prefix when truncated
}

/**
 * Reusable address bar component for breadcrumb navigation
 * Used in both Explorer and Document viewer
 *
 * Supports responsive truncation based on container width:
 * - Wide (>800px): Shows all segments
 * - Medium (600-800px): Shows all segments if ≤3, otherwise last 2
 * - Narrow (<600px): Shows last 2 segments with ellipsis
 */
export function AddressBar({
  segments,
  className = "",
  separator = <ChevronRight className="h-4 w-4" />,
  containerWidth = null,
  maxSegments,
  showEllipsis = true
}: AddressBarProps) {
  if (segments.length === 0) {
    return null
  }

  // Determine visible segments based on container width
  const visibleSegments = useMemo(() => {
    // If maxSegments is explicitly set, use it
    if (maxSegments !== undefined) {
      if (segments.length <= maxSegments) {
        return { segments, isTruncated: false }
      }
      return {
        segments: segments.slice(-maxSegments),
        isTruncated: true
      }
    }

    // Auto-determine based on container width
    if (containerWidth === null) {
      // No width info, show all segments
      return { segments, isTruncated: false }
    }

    if (containerWidth > 800) {
      // Wide: show all segments
      return { segments, isTruncated: false }
    } else if (containerWidth > 600) {
      // Medium: show all if ≤3 segments, otherwise last 2
      if (segments.length <= 3) {
        return { segments, isTruncated: false }
      }
      return {
        segments: segments.slice(-2),
        isTruncated: true
      }
    } else {
      // Narrow: show last 2 segments
      if (segments.length <= 2) {
        return { segments, isTruncated: false }
      }
      return {
        segments: segments.slice(-2),
        isTruncated: true
      }
    }
  }, [segments, containerWidth, maxSegments])

  /** Breadcrumb segments collapsed by truncation, listed in the ellipsis dropdown. */
  const hiddenSegments = useMemo(() => {
    if (!visibleSegments.isTruncated) return []
    const visibleCount = visibleSegments.segments.length
    return segments.slice(0, segments.length - visibleCount)
  }, [segments, visibleSegments])

  // Generate full breadcrumb path for tooltip
  const fullPath = segments.map(s => s.label).join(" > ")

  return (
    <div
      className={`text-sm flex items-center gap-2 text-muted-foreground ${className}`}
      title={visibleSegments.isTruncated ? `Full path: ${fullPath}` : undefined}
    >
      {visibleSegments.isTruncated && showEllipsis && (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger
              type="button"
              className="text-muted-foreground/70 hover:text-foreground transition-colors px-0.5 rounded-sm"
              aria-label={`Show ${hiddenSegments.length} hidden breadcrumb segment${hiddenSegments.length === 1 ? "" : "s"}`}
            >
              ...
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
              collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
            >
              {hiddenSegments.map((segment, index) => (
                <DropdownMenuItem
                  key={segment.path || `hidden-segment-${index}`}
                  onClick={() => segment.onClick?.()}
                  disabled={segment.path === null && segment.onClick === undefined}
                >
                  {segment.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {separator}
        </>
      )}
      {visibleSegments.segments.map((segment, index) => {
        const isLast = index === visibleSegments.segments.length - 1
        const isClickable = typeof segment.onClick === 'function'
        const originalIndex = segments.indexOf(segment)

        return (
          <React.Fragment key={segment.path || `segment-${originalIndex}`}>
            {isClickable ? (
              <>
                <button
                  onClick={(e) => segment.onClick?.(e)}
                  className="hover:text-foreground transition-colors truncate max-w-[200px]"
                  aria-label={`Navigate to ${segment.label}`}
                  title={visibleSegments.isTruncated ? fullPath : undefined}
                >
                  {segment.label}
                </button>
                {!isLast && separator}
              </>
            ) : (
              <>
                <span
                  className={isLast ? "text-foreground truncate max-w-[200px]" : "truncate max-w-[200px]"}
                  title={visibleSegments.isTruncated ? fullPath : undefined}
                >
                  {segment.label}
                </span>
                {!isLast && separator}
              </>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

